/* src/utils/tracking.js
 * Frontend tracking utilities - browser-side pixel injection and event firing
 * Separate from backend adapters for clean separation
 * Supports Direct (pixels/tags) and GTM (dataLayer push for all platforms).
 */
/* global fbq, ttq -- injected at runtime by the platform pixel scripts */

/**
 * Ensure dataLayer exists (used by gtag and GTM)
 */
function ensureDataLayer() {
  if (typeof window === 'undefined') return;
  window.dataLayer = window.dataLayer || [];
}

/**
 * Inject Google Tag Manager container (GTM-XXXXXX).
 * Used when implementationMethod === 'gtm' for any platform.
 */
export function injectGTM(containerId) {
  if (typeof window === 'undefined' || !containerId || !/^GTM-[A-Z0-9]+$/i.test(containerId)) return;
  if (window.google_tag_manager && window.google_tag_manager[containerId]) return;

  ensureDataLayer();
  window.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${containerId}`;
  document.head.appendChild(script);

  const noscript = document.createElement('noscript');
  const iframe = document.createElement('iframe');
  iframe.src = `https://www.googletagmanager.com/ns.html?id=${containerId}`;
  iframe.height = '0';
  iframe.width = '0';
  iframe.style.display = 'none';
  iframe.style.visibility = 'hidden';
  noscript.appendChild(iframe);
  document.body.insertBefore(noscript, document.body.firstChild);

  console.log('[GTM] Container injected:', containerId);
}

/**
 * Push form conversion to dataLayer for GTM. GTM tags can then fire to Meta, Google, etc.
 */
export function pushGTMConversionEvent(pixel, formId, formName, formData = {}) {
  if (typeof window === 'undefined') return;

  ensureDataLayer();
  window.dataLayer.push({
    event: 'form_submit',
    conversionType: 'lead',
    platform: pixel.platform,
    formId: formId || undefined,
    formName: formName || undefined,
    eventName: pixel.eventName || 'Lead',
    conversionId: pixel.conversionId,
    conversionLabel: pixel.conversionLabel,
    pixelId: pixel.pixelId,
    partnerId: pixel.partnerId,
    gtmContainerId: pixel.gtmContainerId,
    timestamp: new Date().toISOString(),
  });

  console.log('[GTM] dataLayer push: form_submit', { platform: pixel.platform, formId, formName });
}

/**
 * Inject Google Ads gtag.js
 */
export function injectGoogleAdsPixel(conversionId) {
  if (typeof window === 'undefined' || !conversionId) return;

  // Already loaded (e.g. by another pixel) — just register this conversion ID
  if (typeof window.gtag === 'function') {
    window.gtag('config', conversionId);
    return;
  }

  const script = document.createElement('script');
  script.src = `https://www.googletagmanager.com/gtag/js?id=${conversionId}`;
  script.async = true;
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer || [];
  // Official gtag stub: must push the `arguments` object, NOT an array —
  // gtag.js ignores plain arrays pushed for commands.
  window.gtag = function gtag() {
    window.dataLayer.push(arguments);
  };

  window.gtag('js', new Date());
  window.gtag('config', conversionId);

  console.log('[GOOGLE ADS] Pixel injected:', conversionId);
}

/**
 * Fire Google Ads conversion
 */
export function fireGoogleAdsConversion(conversionId, conversionLabel, eventName = 'conversion') {
  if (typeof window === 'undefined') return;

  // Ensure the gtag stub exists; commands queue safely until gtag.js loads
  if (typeof window.gtag !== 'function') {
    injectGoogleAdsPixel(conversionId);
  }

  const transactionId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  window.gtag('event', 'conversion', {
    'send_to': `${conversionId}/${conversionLabel}`,
    'value': 0,
    'currency': 'USD',
    'transaction_id': transactionId,
  });

  console.log('[GOOGLE ADS] Conversion fired:', { conversionId, conversionLabel, transactionId });
  return transactionId;
}

/**
 * Inject Meta Pixel (or ensure this pixelId is inited if script already loaded)
 */
export function injectMetaPixel(pixelId) {
  if (typeof window === 'undefined' || !pixelId) return;
  if (typeof fbq !== 'undefined') {
    fbq('init', pixelId);
    return;
  }

  (function(f, b, e, v, n, t, s) {
    if (f.fbq) return;
    n = f.fbq = function() {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = !0;
    n.version = '2.0';
    n.queue = [];
    t = b.createElement(e);
    t.async = !0;
    t.src = v;
    s = b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t, s);
  })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');

  fbq('init', pixelId);
  fbq('track', 'PageView');

  console.log('[META] Pixel injected:', pixelId);
}

/**
 * Fire Meta Pixel event
 */
export function fireMetaPixel(pixelId, eventName = 'Lead', eventData = {}, eventId = null) {
  if (typeof window === 'undefined' || typeof fbq === 'undefined') {
    if (typeof window !== 'undefined') {
      injectMetaPixel(pixelId);
      setTimeout(() => fireMetaPixel(pixelId, eventName, eventData, eventId), 500);
    }
    return null;
  }

  const uniqueEventId = eventId || `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  // eventID must be in the 4th options argument — NOT inside event data —
  // or Meta ignores it and browser/server dedup breaks.
  fbq('track', eventName, eventData, { eventID: uniqueEventId });

  console.log('[META] Pixel event fired:', { pixelId, eventName, eventID: uniqueEventId });
  return uniqueEventId;
}

/**
 * Inject LinkedIn Insight Tag
 */
export function injectLinkedInPixel(partnerId) {
  if (typeof window === 'undefined' || window._linkedin_partner_id === partnerId) return;

  window._linkedin_partner_id = partnerId;
  window._linkedin_data_partner_ids = window._linkedin_data_partner_ids || [];
  window._linkedin_data_partner_ids.push(partnerId);

  (function() {
    const s = document.getElementsByTagName('script')[0];
    const b = document.createElement('script');
    b.type = 'text/javascript';
    b.async = true;
    b.src = 'https://snap.licdn.com/li.lms-analytics/insight.min.js';
    if (s && s.parentNode) {
      s.parentNode.insertBefore(b, s);
    }
  })();

  console.log('[LINKEDIN] Insight Tag injected:', partnerId);
}

/**
 * Fire LinkedIn conversion
 */
export function fireLinkedInConversion(conversionId, partnerId, eventName = 'Lead') {
  if (typeof window === 'undefined') return;

  if (window._linkedin_partner_id !== partnerId) {
    injectLinkedInPixel(partnerId);
    setTimeout(() => fireLinkedInConversion(conversionId, partnerId, eventName), 500);
    return;
  }

  if (typeof window._linkedin !== 'undefined') {
    window._linkedin.track(conversionId, {
      conversionType: eventName,
      conversionValue: 0,
    });
    console.log('[LINKEDIN] Conversion fired:', { conversionId, partnerId, eventName });
  }
}

/**
 * Inject TikTok Pixel
 */
export function injectTikTokPixel(pixelId) {
  if (typeof window === 'undefined' || typeof ttq !== 'undefined') return;

  (function() {
    const t = window;
    t.ttq = t.ttq || function() {
      (t.ttq.methods = t.ttq.methods || []).push(arguments);
      (t.ttq.queue = t.ttq.queue || []);
    };
    const script = document.createElement('script');
    script.src = 'https://analytics.tiktok.com/i18n/pixel/events.js';
    script.async = true;
    const firstScript = document.getElementsByTagName('script')[0];
    if (firstScript && firstScript.parentNode) {
      firstScript.parentNode.insertBefore(script, firstScript);
    }
  })();

  ttq.load(pixelId);
  ttq.page();

  console.log('[TIKTOK] Pixel injected:', pixelId);
}

/**
 * Fire TikTok Pixel event
 */
export function fireTikTokPixel(pixelId, eventName = 'SubmitForm', eventData = {}, eventId = null) {
  if (typeof window === 'undefined' || typeof ttq === 'undefined') {
    if (typeof window !== 'undefined') {
      injectTikTokPixel(pixelId);
      setTimeout(() => fireTikTokPixel(pixelId, eventName, eventData, eventId), 500);
    }
    return null;
  }

  const uniqueEventId = eventId || `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  ttq.track(eventName, {
    ...eventData,
    event_id: uniqueEventId, // snake_case for TikTok
  });

  console.log('[TIKTOK] Pixel event fired:', { pixelId, eventName, event_id: uniqueEventId });
  return uniqueEventId;
}

/**
 * Inject all tracking pixels for a form (Direct tags or GTM container).
 * Backward compatible: missing implementationMethod treated as 'direct'.
 */
export function injectAllPixels(pixels) {
  if (!pixels || !Array.isArray(pixels)) return;

  const method = (pixel) => pixel.implementationMethod || 'direct';
  const seenGtmIds = new Set();

  pixels.forEach((pixel) => {
    try {
      if (method(pixel) === 'gtm') {
        if (pixel.gtmContainerId && /^GTM-[A-Z0-9]+$/i.test(pixel.gtmContainerId) && !seenGtmIds.has(pixel.gtmContainerId)) {
          seenGtmIds.add(pixel.gtmContainerId);
          injectGTM(pixel.gtmContainerId);
        }
        return;
      }

      switch (pixel.platform) {
        case 'google_ads':
          if (pixel.conversionId) {
            injectGoogleAdsPixel(pixel.conversionId);
          }
          break;

        case 'meta':
          if (pixel.pixelId) {
            injectMetaPixel(pixel.pixelId);
          }
          break;

        case 'linkedin':
          if (pixel.partnerId) {
            injectLinkedInPixel(pixel.partnerId);
          }
          break;

        case 'tiktok':
          if (pixel.pixelId) {
            injectTikTokPixel(pixel.pixelId);
          }
          break;

        default:
          console.warn(`[TRACKING] Unknown platform: ${pixel.platform}`);
      }
    } catch (error) {
      console.error(`[TRACKING] Error injecting ${pixel.platform} pixel:`, error);
    }
  });
}

/**
 * Fire all conversion events for a form submission (Direct or GTM dataLayer push).
 * Returns event IDs for server-side deduplication (Direct only; GTM does not return IDs here).
 * Backward compatible: missing implementationMethod treated as 'direct'.
 */
export function fireAllConversionEvents(pixels, formData = {}, options = {}) {
  if (!pixels || !Array.isArray(pixels)) return {};

  const eventIds = {};
  const method = (pixel) => pixel.implementationMethod || 'direct';
  const formId = options.formId;
  const formName = options.formName;

  pixels.forEach((pixel) => {
    try {
      if (method(pixel) === 'gtm') {
        if (pixel.gtmContainerId && /^GTM-[A-Z0-9]+$/i.test(pixel.gtmContainerId)) {
          pushGTMConversionEvent(pixel, formId, formName, formData);
        }
        return;
      }

      switch (pixel.platform) {
        case 'google_ads':
          if (pixel.conversionId && pixel.conversionLabel) {
            fireGoogleAdsConversion(
              pixel.conversionId,
              pixel.conversionLabel,
              pixel.eventName || 'conversion'
            );
          }
          break;

        case 'meta':
          if (pixel.pixelId) {
            const eventId = fireMetaPixel(
              pixel.pixelId,
              'Lead',
              {},
              null
            );
            if (eventId) eventIds.meta = eventId;
          }
          break;

        case 'linkedin':
          if (pixel.conversionId && pixel.partnerId) {
            fireLinkedInConversion(
              pixel.conversionId,
              pixel.partnerId,
              pixel.eventName || 'Lead'
            );
          }
          break;

        case 'tiktok':
          if (pixel.pixelId) {
            const eventId = fireTikTokPixel(
              pixel.pixelId,
              pixel.eventName || 'SubmitForm',
              {},
              null
            );
            if (eventId) eventIds.tiktok = eventId;
          }
          break;

        default:
          console.warn(`[TRACKING] Unknown platform: ${pixel.platform}`);
      }
    } catch (error) {
      console.error(`[TRACKING] Error firing ${pixel.platform} event:`, error);
    }
  });

  return eventIds;
}
